import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FaArrowRight, FaBook, FaClock, FaSearch } from "react-icons/fa";
import Title from "../layouts/Title";
import Reveal from "../layouts/Reveal";
import { handbooks } from "../../constants";

const SHOW = 6; // cards on the homepage; the rest live on /handbooks/

// Lazily loaded, so the homepage bundle stays small however many handbooks exist.
let searchIndex = null;
const loadIndex = () =>
  searchIndex
    ? Promise.resolve(searchIndex)
    : fetch("/handbooks/search.json")
        .then((r) => r.json())
        .then((d) => (searchIndex = d));

const searchChapters = (index, query) => {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  return index
    .map((r) => {
      const title = r.t.toLowerCase();
      const rest = `${r.e} ${r.g} ${r.h}`.toLowerCase();
      let score = 0;
      for (const w of words) {
        if (title.includes(w)) score += 3;
        else if (rest.includes(w)) score += 1;
        else return null;
      }
      return { ...r, score };
    })
    .filter(Boolean)
    .sort((a, b) => b.score - a.score)
    .slice(0, 6);
};

const BookCard = ({ book }) => (
  <a
    href={book.url}
    className="card group flex flex-col overflow-hidden h-full hover:-translate-y-1 duration-300"
    style={{ "--book": book.accent.dark.accent }}
  >
    <div
      className="relative h-32 p-5 overflow-hidden"
      style={{ background: `linear-gradient(135deg, ${book.accent.light.accent}, ${book.accent.dark.accent})` }}
    >
      <svg
        viewBox="0 0 24 24"
        className={`bookIcon icon-${book.icon} w-11 h-11 group-hover:scale-110 duration-500`}
        dangerouslySetInnerHTML={{ __html: book.iconSvg }}
      />
      <span className="absolute right-4 -bottom-3 font-titleFont font-extrabold text-7xl text-white/20 leading-none select-none">
        {book.chapterCount}
      </span>
    </div>
    <div className="flex flex-col gap-2 p-5 flex-1">
      <span className="text-[11px] uppercase tracking-[2px] text-gray-500">{book.category}</span>
      <h3 className="text-xl font-bold text-white font-titleFont leading-snug group-hover:text-[color:var(--book)] duration-300">
        {book.title}
      </h3>
      <p className="text-sm text-gray-400 leading-6">{book.subtitle}</p>
      <div className="flex flex-wrap gap-1.5 mt-1">
        {book.tags.slice(0, 3).map((t) => (
          <span key={t} className="text-[11px] px-2.5 py-1 rounded-full bg-black/30 border border-white/5 text-gray-400">
            {t}
          </span>
        ))}
      </div>
      <div className="flex items-center justify-between mt-auto pt-4 text-xs text-gray-500">
        <span className="inline-flex items-center gap-3">
          <span className="inline-flex items-center gap-1.5"><FaBook /> {book.chapterCount} chapters</span>
          <span className="inline-flex items-center gap-1.5"><FaClock /> ~{book.minutes} min</span>
        </span>
        <FaArrowRight className="text-gray-600 group-hover:text-[color:var(--book)] group-hover:translate-x-1 duration-300" />
      </div>
    </div>
  </a>
);

const ChapterSearch = () => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const box = useRef(null);

  useEffect(() => {
    if (!query.trim()) return setResults([]);
    let alive = true;
    loadIndex().then((index) => alive && setResults(searchChapters(index, query)));
    return () => {
      alive = false;
    };
  }, [query]);

  useEffect(() => {
    const close = (e) => box.current && !box.current.contains(e.target) && setOpen(false);
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, []);

  return (
    <div ref={box} className="relative w-full lgl:max-w-md">
      <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm" />
      <input
        value={query}
        onFocus={() => {
          setOpen(true);
          loadIndex();
        }}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
        placeholder="Search every chapter — HNSW, discovery, reranking…"
        aria-label="Search all handbook chapters"
        className="w-full h-12 pl-11 pr-4 rounded-full bg-black/25 border border-white/10 text-sm text-gray-200 placeholder-gray-500 outline-none focus:border-designColor duration-300"
      />
      <AnimatePresence>
        {open && query.trim() && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            className="absolute z-30 left-0 right-0 mt-2 p-2 rounded-2xl bg-[#1b1e22] border border-white/10 shadow-2xl shadow-black/50"
          >
            {results.length ? (
              results.map((r) => (
                <a key={r.u} href={r.u} className="block px-3 py-2.5 rounded-xl hover:bg-white/5 duration-200">
                  <span className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-gray-500">
                    <span className="w-2 h-2 rounded-full" style={{ background: r.c }}></span>
                    {r.h} · {r.g}
                  </span>
                  <span className="block text-sm font-semibold text-white mt-0.5">{r.t}</span>
                  <span className="block text-xs text-gray-400 mt-0.5 clamp-2">{r.e}</span>
                </a>
              ))
            ) : (
              <p className="px-3 py-3 text-sm text-gray-500">No chapters match “{query}”.</p>
            )}
            <a href="/handbooks/" className="block px-3 pt-2 pb-1 text-xs text-designColor hover:underline">
              Browse the full library →
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Library = () => {
  const [category, setCategory] = useState("All");
  const categories = useMemo(() => ["All", ...new Set(handbooks.map((b) => b.category))], []);
  const filtered = handbooks.filter((b) => category === "All" || b.category === category);
  const visible = filtered.slice(0, SHOW);
  const chapters = handbooks.reduce((n, b) => n + b.chapterCount, 0);
  const minutes = handbooks.reduce((n, b) => n + b.minutes, 0);

  return (
    <section id="handbooks" className="sectionWrap">
      <Title title="From My Notebook" des="Handbooks & Notes" />
      <p className="text-center text-gray-400 -mt-8 mb-12">
        {handbooks.length} handbooks · {chapters} chapters · ~{Math.round(minutes / 60)} hours of reading
      </p>

      <div className="flex flex-col lgl:flex-row lgl:items-center gap-4 justify-between mb-10">
        <div className="flex gap-2 overflow-x-auto scrollbar-hide -mx-1 px-1 pb-1">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={`relative shrink-0 px-4 py-2 rounded-full text-sm border duration-300 ${
                category === c ? "text-white border-transparent" : "text-gray-400 border-white/10 hover:text-white"
              }`}
            >
              {category === c && (
                <motion.span
                  layoutId="libraryCat"
                  className="absolute inset-0 rounded-full bg-designColor shadow-lg shadow-designColor/30"
                  transition={{ type: "spring", stiffness: 400, damping: 32 }}
                />
              )}
              <span className="relative">{c}</span>
            </button>
          ))}
        </div>
        <ChapterSearch />
      </div>

      <Reveal>
        <motion.div layout className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {visible.map((book) => (
              <motion.div
                key={book.slug}
                layout
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.25 }}
              >
                <BookCard book={book} />
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </Reveal>

      <div className="flex justify-center mt-10">
        <a
          href="/handbooks/"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/10 text-gray-300 hover:border-designColor hover:text-white duration-300"
        >
          {filtered.length > SHOW ? `Browse all ${filtered.length} handbooks` : "Open the library"}
          <FaArrowRight className="text-xs" />
        </a>
      </div>
    </section>
  );
};

export default Library;
